import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  InlineStack,
  BlockStack,
  Text,
  Pagination,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
  Select,
  TextField,
  Banner,
  Filters,
} from '@shopify/polaris';
import { billingApi, clinicsApi } from '@/services';
import SearchableSelect from '@/components/SearchableSelect';
import type { ClinicInvoice, InvoiceStatus, Clinic } from '@/types';

type BadgeTone = 'success' | 'warning' | 'critical' | 'info' | undefined;

const STATUS_BADGE_CONFIG: Record<InvoiceStatus, { tone: BadgeTone; label: string }> = {
  draft: { tone: undefined, label: 'Draft' },
  pending: { tone: 'info', label: 'Pending' },
  sent: { tone: 'info', label: 'Sent' },
  paid: { tone: 'success', label: 'Paid' },
  overdue: { tone: 'critical', label: 'Overdue' },
  voided: { tone: 'critical', label: 'Voided' },
};

function getStatusBadge(status: InvoiceStatus) {
  const config = STATUS_BADGE_CONFIG[status] ?? { tone: undefined, label: status };
  return <Badge tone={config.tone}>{config.label}</Badge>;
}

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Pending', value: 'pending' },
  { label: 'Sent', value: 'sent' },
  { label: 'Paid', value: 'paid' },
  { label: 'Overdue', value: 'overdue' },
  { label: 'Voided', value: 'voided' },
];

export default function InvoicesList() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [clinicSearch, setClinicSearch] = useState('');
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidTargetId, setVoidTargetId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [errorBanner, setErrorBanner] = useState('');
  const limit = 20;

  // Generate invoice form state
  const [generateClinicId, setGenerateClinicId] = useState('');
  const [generatePeriodStart, setGeneratePeriodStart] = useState('');
  const [generatePeriodEnd, setGeneratePeriodEnd] = useState('');

  // Fetch invoices
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', page, statusFilter, clinicSearch],
    queryFn: () =>
      billingApi.listInvoices({
        page,
        limit,
        status: statusFilter || undefined,
        clinicId: clinicSearch || undefined,
      }),
  });

  // Clinics search on the server. The previous capped fetch put roughly a quarter of
  // the 842 clinics into a native Select, with no way to type or reach the rest.
  const [generateClinicName, setGenerateClinicName] = useState('');
  const searchClinics = useCallback(async (query: string) => {
    const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
    return res.data.map((c: Clinic) => ({
      label: c.name,
      value: c.id,
      detail: c.email || undefined,
    }));
  }, []);

  // Mutations
  const generateMutation = useMutation({
    mutationFn: () =>
      billingApi.generateInvoice({
        clinicId: generateClinicId,
        periodStart: generatePeriodStart,
        periodEnd: generatePeriodEnd,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setGenerateModalOpen(false);
      resetGenerateForm();
    },
    onError: (err: any) => {
      setErrorBanner(err?.response?.data?.message || 'Failed to generate invoice');
    },
  });

  const sendMutation = useMutation({
    mutationFn: (id: string) => billingApi.markSent(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
    onError: (err: any) => {
      setErrorBanner(err?.response?.data?.message || 'Failed to send invoice');
    },
  });

  const payMutation = useMutation({
    mutationFn: (id: string) => billingApi.markPaid(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
    onError: (err: any) => {
      setErrorBanner(err?.response?.data?.message || 'Failed to mark invoice as paid');
    },
  });

  const voidMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      billingApi.voidInvoice(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setVoidModalOpen(false);
      setVoidTargetId(null);
      setVoidReason('');
    },
    onError: (err: any) => {
      setErrorBanner(err?.response?.data?.message || 'Failed to void invoice');
    },
  });

  const resetGenerateForm = useCallback(() => {
    setGenerateClinicId('');
    setGeneratePeriodStart('');
    setGeneratePeriodEnd('');
  }, []);

  const handleOpenVoidModal = useCallback((id: string) => {
    setVoidTargetId(id);
    setVoidReason('');
    setVoidModalOpen(true);
  }, []);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const handleClinicSearchChange = useCallback((value: string) => {
    setClinicSearch(value);
    setPage(1);
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    setStatusFilter('');
    setClinicSearch('');
    setPage(1);
  }, []);

  const formatPeriod = (start: string, end: string) => {
    const s = new Date(start).toLocaleDateString();
    const e = new Date(end).toLocaleDateString();
    return `${s} - ${e}`;
  };

  const renderActionButtons = (invoice: ClinicInvoice) => {
    const buttons: React.ReactNode[] = [];
    const status = invoice.status;

    if (status === 'draft' || status === 'pending') {
      buttons.push(
        <Button
          key={`send-${invoice.id}`}
          size="slim"
          onClick={() => sendMutation.mutate(invoice.id)}
          loading={sendMutation.isPending}
        >
          Send
        </Button>,
      );
    }

    if (status === 'sent' || status === 'pending') {
      buttons.push(
        <Button
          key={`pay-${invoice.id}`}
          size="slim"
          variant="primary"
          onClick={() => payMutation.mutate(invoice.id)}
          loading={payMutation.isPending}
        >
          Mark Paid
        </Button>,
      );
    }

    if (status !== 'voided' && status !== 'paid') {
      buttons.push(
        <Button
          key={`void-${invoice.id}`}
          size="slim"
          tone="critical"
          onClick={() => handleOpenVoidModal(invoice.id)}
        >
          Void
        </Button>,
      );
    }

    buttons.push(
      <Button
        key={`print-${invoice.id}`}
        size="slim"
        url={billingApi.getPrintUrl(invoice.id)}
        target="_blank"
      >
        Print
      </Button>,
    );

    return <InlineStack gap="200">{buttons}</InlineStack>;
  };

  const rows = (data?.data || []).map((invoice: ClinicInvoice) => [
    invoice.invoiceNumber,
    invoice.clinic?.name || invoice.clinicId,
    formatPeriod(invoice.periodStart, invoice.periodEnd),
    `$${Number(invoice.subtotal).toFixed(2)}`,
    `$${Number(invoice.taxAmount).toFixed(2)}`,
    `$${Number(invoice.totalAmount).toFixed(2)}`,
    getStatusBadge(invoice.status),
    renderActionButtons(invoice),
  ]);

  if (error) {
    return (
      <Page
        title="Invoices"
        primaryAction={{
          content: 'Generate Invoice',
          onAction: () => setGenerateModalOpen(true),
        }}
      >
        <Card>
          <EmptyState
            heading="Error loading invoices"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Invoices"
      primaryAction={{
        content: 'Generate Invoice',
        onAction: () => setGenerateModalOpen(true),
      }}
    >
      {errorBanner && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" onDismiss={() => setErrorBanner('')}>
            {errorBanner}
          </Banner>
        </Box>
      )}

      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={clinicSearch}
            queryPlaceholder="Search by clinic..."
            onQueryChange={handleClinicSearchChange}
            onQueryClear={() => handleClinicSearchChange('')}
            onClearAll={handleFiltersClearAll}
            filters={[
              {
                key: 'status',
                label: 'Status',
                filter: (
                  <Select
                    label="Status"
                    labelHidden
                    options={STATUS_OPTIONS}
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                  />
                ),
                shortcut: true,
              },
            ]}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No invoices found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              action={{
                content: 'Generate Invoice',
                onAction: () => setGenerateModalOpen(true),
              }}
            >
              <p>Generate your first invoice to get started.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'numeric',
                'numeric',
                'numeric',
                'text',
                'text',
              ]}
              headings={[
                'Invoice #',
                'Clinic',
                'Period',
                'Subtotal',
                'Tax',
                'Total',
                'Status',
                'Actions',
              ]}
              rows={rows}
            />
            {data && data.meta?.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {(data.data || []).length} of {data.meta?.total ?? 0} invoices
          </Text>
        </Box>
      )}

      {/* Generate Invoice Modal */}
      <Modal
        open={generateModalOpen}
        onClose={() => {
          setGenerateModalOpen(false);
          resetGenerateForm();
        }}
        title="Generate Invoice"
        primaryAction={{
          content: 'Generate',
          onAction: () => generateMutation.mutate(),
          loading: generateMutation.isPending,
          disabled: !generateClinicId || !generatePeriodStart || !generatePeriodEnd,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setGenerateModalOpen(false);
              resetGenerateForm();
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <SearchableSelect
              label="Clinic"
              value={generateClinicId}
              selectedLabel={generateClinicName}
              onChange={(value, option) => {
                setGenerateClinicId(value);
                setGenerateClinicName(option?.label ?? '');
              }}
              onSearch={searchClinics}
              emptyOption={{ label: 'Select a clinic', value: '' }}
              placeholder="Type a clinic name…"
            />
            <TextField
              label="Period Start"
              type="date"
              value={generatePeriodStart}
              onChange={setGeneratePeriodStart}
              autoComplete="off"
            />
            <TextField
              label="Period End"
              type="date"
              value={generatePeriodEnd}
              onChange={setGeneratePeriodEnd}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Void Invoice Modal */}
      <Modal
        open={voidModalOpen}
        onClose={() => {
          setVoidModalOpen(false);
          setVoidTargetId(null);
          setVoidReason('');
        }}
        title="Void Invoice"
        primaryAction={{
          content: 'Void Invoice',
          onAction: () => {
            if (voidTargetId) {
              voidMutation.mutate({ id: voidTargetId, reason: voidReason });
            }
          },
          loading: voidMutation.isPending,
          disabled: !voidReason.trim(),
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setVoidModalOpen(false);
              setVoidTargetId(null);
              setVoidReason('');
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="warning">
              This action cannot be undone. The invoice will be permanently voided.
            </Banner>
            <TextField
              label="Reason for voiding"
              value={voidReason}
              onChange={setVoidReason}
              multiline={3}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
